"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 27: VRC4 variant (World Hero / unlicensed)
 * ------------------------------------------------------------------ *
 * The board wires the VRC4's address lines differently. The register
 * map is:
 *
 *   $8000     PRG bank at $8000 (or $C000 if $9002 bit 2 is set)
 *   $9000     nametable arrangement (2 bits)
 *   $9002     PRG swap mode (bit 2) — 1 = $8000 reg maps to $C000
 *   $A000     PRG bank at $A000
 *   $B000-$E003  eight 1K CHR windows, each 9 bits:
 *               even offset -> low 4 bits, odd offset -> high 5 bits
 *   $F000     IRQ latch low 4 bits
 *   $F001     IRQ latch high 4 bits
 *   $F002     IRQ control: bit 1 = enable (E), bit 0 = retrigger (R)
 *   $F003     IRQ acknowledge (copies R into E)
 *
 * IRQ is MMC3-style: triggered by PPU A12 rising edges (once per scanline).
 * The counter counts up; a wrap from $FF reloads the latch and asserts IRQ.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  function Mapper27(nes) {
    this.nes = nes;
  }

  Mapper27.prototype = new Mappers[0]();

  // The IRQ is driven by PPU A12 edges (like MMC3), so we hook the
  // scanline counter through the standard MMC3 helper.
  Mapper27.prototype.watchesA12 = true;

  // The chip drives its own mirroring via $9000.
  Mapper27.prototype.ownsMirroring = true;

  Mapper27.prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // PRG: two 8K switchable windows at $8000 and $A000.
    // $C000 and $E000 are fixed to the last two 8K banks unless swap
    // mode moves the $8000 register to $C000.
    this.prgBank0 = 0; // 5 bits
    this.prgBank1 = 0; // 5 bits
    this.prgSwap = false; // $9002 bit 2

    // CHR: eight 9-bit 1K banks for PPU $0000-$1FFF.
    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];

    // $9000 nametable arrangement (0=V, 1=H, 2=1-screen low, 3=1-screen high)
    this.mirroring = 0;

    // IRQ: MMC3-style A12 counter.
    this.irqLatch = 0;
    this.irqCounter = 0;
    this.irqEnabled = false; // $F002 bit 1 (E)
    this.irqEnableAck = false; // $F002 bit 0 (R)
  };

  Mapper27.prototype.write = function (address, value) {
    if (address < 0x8000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    var page = address & 0xf000;
    switch (page) {
      case 0x8000:
        // PRG Select 0: 8K window at $8000 (or $C000 under swap mode).
        // The wiki says register is selected by low address bits (like VRC4),
        // but only one register per $1000 page here. Use bit 0 as in VRC4?
        // The wiki: "8000: xxxxxxxx = Switches 8k PRG bank 8000-9FFF (or C000-DFFF)"
        // No register select mentioned for $8000 page — assume direct write.
        this.prgBank0 = value & 0x1f;
        this.updatePrgBanks();
        break;

      case 0x9000:
        // $9000: mirroring (bits 0-1)
        // $9002: swap mode (bit 2)
        if ((address & 0x0003) === 0) {
          // $9000/$9004/$9008/$900C -> mirroring
          this.mirroring = value & 0x03;
          this.applyMirroring();
        } else if ((address & 0x0003) === 2) {
          // $9002/$9006/$900A/$900E -> swap mode
          this.prgSwap = (value & 0x04) !== 0;
          this.updatePrgBanks();
        }
        break;

      case 0xa000:
        // PRG Select 1: 8K window at $A000.
        this.prgBank1 = value & 0x1f;
        this.updatePrgBanks();
        break;

      case 0xf000:
        this.writeIrq(address & 3, value);
        break;

      default:
        // $B000-$EFFF: CHR banks. Each $1000 page has 4 registers (2 windows × 2 halves).
        // Window index = ((page - 0xB000) / 0x1000) * 2 + (offset & 2) >> 1
        // Even offset (bit 0 clear) = low 4 bits, odd = high 5 bits.
        if (page >= 0xb000 && page <= 0xe000) {
          var offset = address & 3;
          var window = ((page - 0xb000) >> 12) * 2 + (offset >> 1);
          if (offset & 1) {
            // High 5 bits
            this.chrBanks[window] =
              (this.chrBanks[window] & 0x0f) | ((value & 0x1f) << 4);
          } else {
            // Low 4 bits
            this.chrBanks[window] =
              (this.chrBanks[window] & 0x1f0) | (value & 0x0f);
          }
          this.load1kVromBank(this.chrBanks[window], window * 0x400);
        }
        break;
    }
  };

  // IRQ registers: $F000-$F003 (one per offset 0-3)
  Mapper27.prototype.writeIrq = function (reg, value) {
    switch (reg) {
      case 0:
        // $F000: latch low 4 bits
        this.irqLatch = (this.irqLatch & 0xf0) | (value & 0x0f);
        break;

      case 1:
        // $F001: latch high 4 bits
        this.irqLatch = (this.irqLatch & 0x0f) | ((value & 0x0f) << 4);
        break;

      case 2:
        // $F002: control: bit 1 = E (enable), bit 0 = R (retrigger)
        // Any write acknowledges pending IRQ and reloads counter.
        this.irqEnabled = (value & 0x02) !== 0;
        this.irqEnableAck = (value & 0x01) !== 0;
        this.irqCounter = this.irqLatch;
        break;

      default:
        // $F003: acknowledge — copy R into E
        this.irqEnabled = this.irqEnableAck;
        break;
    }
  };

  // A12 edge callback: one clock per rising edge.
  Mapper27.prototype.clockA12 = function () {
    if (!this.irqEnabled) {
      return;
    }
    if (this.irqCounter === 0xff) {
      this.irqCounter = this.irqLatch;
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    } else {
      this.irqCounter++;
    }
  };

  Mapper27.prototype.updatePrgBanks = function () {
    var count8k = this.nes.rom.romCount * 2; // 16K banks → 8K banks
    // $E000-$FFFF: last 8K bank (fixed)
    this.load8kRomBank(count8k - 1, 0xe000);
    if (this.prgSwap) {
      // Swap: $8000 register controls $C000, $C000 fixed at second-to-last
      this.load8kRomBank(count8k - 2, 0x8000);
      this.load8kRomBank(this.prgBank0, 0xc000);
    } else {
      this.load8kRomBank(this.prgBank0, 0x8000);
      this.load8kRomBank(count8k - 2, 0xc000);
    }
    this.load8kRomBank(this.prgBank1, 0xa000);
  };

  Mapper27.prototype.updateChrBanks = function () {
    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(this.chrBanks[i], i * 0x400);
    }
  };

  Mapper27.prototype.applyMirroring = function () {
    var types = this.nes.ppu.MIRRORING_TYPES;
    var rom = this.nes.rom;
    switch (this.mirroring) {
      case 0:
        this.setMirroring(rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(types.SINGLE_SCREEN);
        break;
      default:
        this.setMirroring(types.SINGLE_SCREEN_ALT);
        break;
    }
  };

  Mapper27.prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Mapper 27: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on: last 8K bank fixed at $E000, second-to-last at $C000.
    this.updatePrgBanks();
    this.updateChrBanks();

    // Seed mirroring from header.
    this.mirroring =
      this.nes.rom.getMirroringType() === this.nes.rom.HORIZONTAL_MIRRORING
        ? 1
        : 0;
    this.applyMirroring();

    this.resetIrqHandler();
  };

  Mapper27.prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank0 = this.prgBank0;
    s.prgBank1 = this.prgBank1;
    s.prgSwap = this.prgSwap;
    s.chrBanks = this.chrBanks.slice();
    s.mirroring = this.mirroring;
    s.irqLatch = this.irqLatch;
    s.irqCounter = this.irqCounter;
    s.irqEnabled = this.irqEnabled;
    s.irqEnableAck = this.irqEnableAck;
    return s;
  };

  Mapper27.prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank0 = s.prgBank0;
    this.prgBank1 = s.prgBank1;
    this.prgSwap = s.prgSwap;
    this.chrBanks = s.chrBanks.slice();
    this.mirroring = s.mirroring;
    this.irqLatch = s.irqLatch;
    this.irqCounter = s.irqCounter;
    this.irqEnabled = s.irqEnabled;
    this.irqEnableAck = s.irqEnableAck;
    this.updatePrgBanks();
    this.updateChrBanks();
    this.applyMirroring();
  };

  Mappers[27] = Mapper27;
}

module.exports = { register: register };
